import { getMraidVersion } from 'common/js/mraid-utils';
import * as React from 'react';
import { useMraidListener } from './mraid-listener';

export function useMRAIDReady() {
  const [isMraidReady, setIsMraidReady] = React.useState(false);

  if (window.mraid.getState() !== 'loading') {
    setIsMraidReady(true);
  }
  useMraidListener('ready', () => setIsMraidReady(true));

  return isMraidReady;
}

export function useOnReady(cb: () => void) {
  const mraidReady = useMRAIDReady();
  const [isMraidReady, setIsMraidReady] = React.useState(false);

  if (mraidReady && isMraidReady === false) {
    setIsMraidReady(true);
    cb();
  }
}

/**
 * Shortcut to use both viewableChange && exposureChange
 */
export function useMRAIDViewability() {
  const { isViewable } = useMRAIDExposureChange();
  const deprecatedIsViewable = useMRAIDViewableChange();

  return getMraidVersion() >= 3 ? isViewable : deprecatedIsViewable;
}

export function useOnFirstViewable(cb: () => void) {
  const isViewable = useMRAIDViewability();
  const [isMraidViewable, setIsMraidViewable] = React.useState(false);

  if (isViewable && isMraidViewable === false) {
    setIsMraidViewable(true);
    cb();
  }
}

export function useMRAIDViewableChange() {
  function isViewable(testValue?: boolean | 'true' | 'false') {
    const viewable = testValue || window.mraid.isViewable();
    return viewable === true || viewable === 'true';
  }

  const [isMraidViewable, setIsMraidViewable] = React.useState(isViewable());
  useMraidListener('viewableChange', newViewability => {
    setIsMraidViewable(isViewable(newViewability));
  });
  return isMraidViewable;
}

export function useMRAIDExposureChange() {
  const [exposedPercentage, setExposedPercentage] = React.useState(0);
  const [
    visibleRectangle,
    setVisibleRectangle
  ] = React.useState<MRAIDPosition | void>();
  const [occlusionRectangles, setOcclusionRectangles] = React.useState<
    MRAIDPosition[] | void
  >();

  useMraidListener(
    'exposureChange',
    (
      newExposedPercentage: number,
      newVisibleRectangle?: MRAIDPosition,
      newOcclusionRectangles?: MRAIDPosition[]
    ) => {
      setExposedPercentage(newExposedPercentage);
      setVisibleRectangle(newVisibleRectangle);
      setOcclusionRectangles(newOcclusionRectangles);
    }
  );

  return {
    isViewable: exposedPercentage >= 50,
    exposedPercentage,
    visibleRectangle,
    occlusionRectangles
  };
}

export function useMRAIDStateChange() {
  const [mraidState, setMraidState] = React.useState(window.mraid.getState());

  useMraidListener('stateChange', (newState: MRAIDState) =>
    setMraidState(newState)
  );

  return mraidState;
}
